import { createFileRoute } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useEffect, useState, useRef } from 'react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { ExternalAuthMessage, ExternalAuthResponse } from '@wtf/shared'

// Type declaration for Chrome extension messaging
declare const chrome: {
    runtime: {
        sendMessage: (
            extensionId: string,
            message: ExternalAuthMessage,
            callback?: (response: ExternalAuthResponse) => void
        ) => void
        lastError?: { message: string }
    }
}

type ExtensionAuthCheckSearchParams = {
    state?: string
    source?: string
    redirect_uri?: string
}

export const Route = createFileRoute(
    '/(onboarding)/(auth)/(extension)/extension-auth-check'
)({
    validateSearch: (
        search: Record<string, unknown>
    ): ExtensionAuthCheckSearchParams => {
        return {
            state: typeof search.state === 'string' ? search.state : undefined,
            source:
                typeof search.source === 'string' ? search.source : undefined,
            redirect_uri:
                typeof search.redirect_uri === 'string'
                    ? search.redirect_uri
                    : undefined,
        }
    },
    component: ExtensionAuthCheckRoute,
})

function ExtensionAuthCheckRoute() {
    const { state, source, redirect_uri } = Route.useSearch()
    const { user, loading } = useCurrentUser()
    const [status, setStatus] = useState<'checking' | 'redirecting' | 'error'>(
        'checking'
    )
    const [message, setMessage] = useState('Checking authentication status...')
    const authSentRef = useRef(false)

    useEffect(() => {
        handleAuthCheck()
    }, [user, loading, state, source])

    const handleAuthCheck = async () => {
        try {
            // Validate that this is coming from the extension
            if (source !== 'extension') {
                throw new Error('Invalid source parameter')
            }

            // Wait for auth to finish loading before making decisions
            if (loading) {
                console.log('Waiting for Firebase auth to initialize...')
                return
            }

            if (user) {
                // Check if token was already sent to prevent duplicates
                if (authSentRef.current) {
                    console.log(
                        '[Extension Auth] Token already sent, skipping duplicate'
                    )
                    return
                }

                // User is already authenticated - get tokens and auto-redirect
                console.log('User authenticated, generating custom token...')
                setStatus('redirecting')
                setMessage('Already authenticated! Redirecting...')

                // Mark as sent BEFORE calling to prevent race conditions
                authSentRef.current = true
                await handleAutoAuth()
            } else {
                // User not authenticated - redirect to login
                console.log('User not authenticated, redirecting to login...')
                setStatus('redirecting')
                setMessage('Authentication required. Redirecting to login...')
                redirectToLogin()
            }
        } catch (error) {
            console.error('Extension auth check error:', error)
            setStatus('error')
            setMessage(
                error instanceof Error
                    ? error.message
                    : 'Authentication check failed'
            )

            // Redirect to login as fallback after error
            setTimeout(() => {
                redirectToLogin()
            }, 3000)
        }
    }

    const getCustomTokenFromAPI = async (): Promise<string | null> => {
        try {
            // Get current user's ID token
            const { getAuth } = await import('firebase/auth')
            const auth = getAuth()
            const currentUser = auth.currentUser

            if (!currentUser) {
                throw new Error('No authenticated user found')
            }

            const idToken = await currentUser.getIdToken()

            // Call API to generate custom token
            const response = await fetch(
                `${import.meta.env.VITE_API_URL}/api/auth/extension/generate-custom-token`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Failed to generate custom token')
            }

            const data = await response.json()
            return data.customToken
        } catch (error) {
            console.error('Failed to get custom token:', error)
            return null
        }
    }

    /**
     * Parse extension IDs from environment variable
     * Supports comma or space separated values
     */
    const getExtensionIds = (): string[] => {
        const extensionIdEnv = import.meta.env.VITE_EXTENSION_ID
        if (!extensionIdEnv) {
            return []
        }
        // Split by comma or space, trim whitespace, filter empty strings
        return extensionIdEnv
            .split(/[,\s]+/)
            .map((id: string) => id.trim())
            .filter((id: string) => id.length > 0)
    }

    /**
     * Try to send a message to one or more extension IDs
     * Returns a promise that resolves when the first extension responds successfully
     */
    const trySendToExtensions = async (
        message: ExternalAuthMessage
    ): Promise<{ success: boolean; extensionId?: string; response?: ExternalAuthResponse }> => {
        const extensionIds = getExtensionIds()

        if (extensionIds.length === 0) {
            return { success: false }
        }

        if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
            return { success: false }
        }

        // Try each extension ID sequentially until one succeeds
        for (const extensionId of extensionIds) {
            try {
                console.log(
                    `[Extension Auth] Trying extension ID: ${extensionId}`
                )

                const response = await new Promise<ExternalAuthResponse | null>(
                    (resolve) => {
                        chrome.runtime.sendMessage(
                            extensionId,
                            message,
                            (response: ExternalAuthResponse | undefined) => {
                                if (chrome.runtime.lastError) {
                                    console.warn(
                                        `[Extension Auth] Extension ${extensionId} not available:`,
                                        chrome.runtime.lastError.message
                                    )
                                    resolve(null)
                                } else {
                                    resolve(response || null)
                                }
                            }
                        )
                    }
                )

                if (response && response.success) {
                    console.log(
                        `[Extension Auth] Successfully connected to extension: ${extensionId}`,
                        response
                    )
                    return { success: true, extensionId, response }
                }
            } catch (error) {
                console.warn(
                    `[Extension Auth] Failed to send to extension ${extensionId}:`,
                    error
                )
                // Continue to next extension ID
            }
        }

        // None of the extension IDs worked
        console.warn(
            '[Extension Auth] Could not connect to any extension. Tried IDs:',
            extensionIds
        )
        return { success: false }
    }

    const handleAutoAuth = async () => {
        try {
            // Get custom token from API
            const customToken = await getCustomTokenFromAPI()
            if (!customToken) {
                throw new Error('Failed to generate custom token')
            }

            // Try to send token to extension(s)
            const result = await trySendToExtensions({
                type: 'AUTH_TOKEN',
                customToken: customToken,
                state: state,
            })

            if (result.success) {
                // Update UI to show success
                setStatus('checking')
                setMessage(
                    'Authentication successful! The extension will close this tab shortly.'
                )

                // Don't navigate away immediately - let extension close tab
                // If extension doesn't close tab within 5 seconds, show message
                setTimeout(() => {
                    setMessage(
                        'Authentication successful! You can close this tab.'
                    )
                }, 5000)

                return
            }

            // Fallback: Original redirect logic for launchWebAuthFlow or other flows
            if (redirect_uri) {
                // Production flow: Use launchWebAuthFlow redirect
                const callbackUrl = new URL(redirect_uri)
                const hashParams = new URLSearchParams()
                hashParams.set('token', customToken)
                if (state) {
                    hashParams.set('state', state)
                }
                callbackUrl.hash = hashParams.toString()

                console.log(
                    'Redirecting to extension with custom token:',
                    callbackUrl.toString()
                )
                window.location.href = callbackUrl.toString()
            } else {
                // Local development flow without extension: Update URL with token in hash
                const currentUrl = new URL(window.location.href)
                const hashParams = new URLSearchParams()
                hashParams.set('token', customToken)
                if (state) {
                    hashParams.set('state', state)
                }
                currentUrl.hash = hashParams.toString()

                console.log('Tab-based auth: Setting custom token in URL hash')
                window.location.href = currentUrl.toString()

                setStatus('checking')
                setMessage('Authentication successful! You can close this tab.')
            }
        } catch (error) {
            console.error('Auto-auth failed:', error)
            throw error
        }
    }

    const redirectToLogin = () => {
        try {
            // Notify extension(s) that user needs to see the login page
            trySendToExtensions({ type: 'LOGIN_REQUIRED' }).then((result) => {
                if (result.success) {
                    console.log(
                        '[Extension Auth] Extension notified:',
                        result.response
                    )
                }
            })

            // Redirect to login, with return path back to this auth check
            const loginUrl = new URL('/login', window.location.origin)
            const returnPath = `/extension-auth-check?source=extension${state ? `&state=${state}` : ''}${redirect_uri ? `&redirect_uri=${encodeURIComponent(redirect_uri)}` : ''}`
            loginUrl.searchParams.set(
                'returnToPath',
                encodeURIComponent(returnPath)
            )

            console.log('Redirecting to login with return path')
            window.location.href = loginUrl.toString()
        } catch (error) {
            console.error('Failed to redirect to login:', error)
        }
    }

    return (
        <div className="flex min-h-dvh items-center justify-center bg-linear-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 pt-safe">
            <div className="w-full max-w-md rounded-xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur-md">
                <div className="mb-6">
                    <img
                        src={assetPath('images/rectangle-logo.png')}
                        alt="Style Collection"
                        className="mx-auto mb-4 h-8"
                    />
                    <h1 className="mb-2 text-2xl font-semibold text-white">
                        Extension Authentication
                    </h1>
                </div>

                <div className="mb-6">
                    {status === 'checking' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
                        </div>
                    )}

                    {status === 'redirecting' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="animate-pulse rounded-full bg-blue-500 p-2">
                                <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 7l5 5-5 5M6 7l5 5-5 5"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="rounded-full bg-red-500 p-2">
                                <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}

                    <p className="text-sm text-white/80">{message}</p>
                </div>

                {status === 'error' && (
                    <div className="text-xs text-white/60">
                        Redirecting to login page...
                    </div>
                )}
            </div>
        </div>
    )
}
